import {AfterViewInit, ChangeDetectorRef, Component, inject, model, OnInit, signal, ViewChild} from '@angular/core';
import {
  ButtonComponent,
  ButtonOptions,
  DateComponent, DateOptions,
  FormComponent,
  ModalComponent,
  ModalOptions
} from "@shared/components";
import {DropdownComponent, DropdownOptions} from "@shared/components/dropdown/dropdown.component";
import {NgOptimizedImage} from "@angular/common";
import {
  AbstractControl,
  FormControl,
  FormGroup,
  NgForm,
  ReactiveFormsModule, ValidationErrors,
  ValidatorFn,
  Validators
} from "@angular/forms";
import {assets} from '@core/constants';
import {Coding} from '@core/classes/other/coding';
import {APIService, DataService, ToastService} from '@core/services';
import {PatientService} from '@core/services/patient.service';
import {HTTPMethods, ModalTypes} from '@core/enums';
import {ATC} from '@core/classes/other/atc';
import {HttpHeaders} from '@angular/common/http';
import {DefaultSearchComponent} from '@shared/components/dropdown/search/default-search/default-search.component';
import {lastValueFrom} from 'rxjs';
import {Response, ResponseSingle} from '@core/classes/other/response';
import {MedicalDevice} from '@core/classes/tables/medical-device';
import {checkDate} from '@core/helpers';
import moment from 'moment';

@Component({
  selector: 'ncp-add-device-implant',
    imports: [
        ButtonComponent,
        DateComponent,
        DropdownComponent,
        ModalComponent,
        NgOptimizedImage,
        ReactiveFormsModule
    ],
  templateUrl: './add-device-implant.component.html',
  styleUrl: './add-device-implant.component.scss',
})
export class AddDeviceImplantComponent implements OnInit, AfterViewInit {
  @ViewChild('mod') public mod!: ModalComponent;
  @ViewChild('diFrm') protected devicesFormEl?: NgForm;
  @ViewChild('addDeviceButton') public addDeviceButton!: ButtonComponent;

  @ViewChild( 'deviceDesc') public deviceDesc!: DropdownComponent<Coding>;
  @ViewChild('deviceDate') public deviceDate!: DateComponent;

  @ViewChild('addDeviceButton') getDIbtn  = () => {
    return this.addDeviceButton
  }

  protected readonly toastService = inject(ToastService);
  private readonly changeDetectorRef: ChangeDetectorRef = inject(ChangeDetectorRef);

  private apiService = inject(APIService);
  private patientService = inject(PatientService);
  private dataService = inject(DataService);

  protected data = model<any>();

  private _params = {};
  protected form: FormGroup = new FormGroup({
    coding_id: new FormControl<number | string | undefined>(undefined, [Validators.required]),
    implant_date: new FormControl<string>('', [Validators.required]),
  }, {
    validators: [this.formValidator()]
  });

  formValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let errors: ValidationErrors | null = {};

      let deviceCodingId = (control as FormGroup).controls['coding_id'];
      let implantDate = (control as FormGroup).controls['implant_date'];

      let patientDOB = moment(this.patientService.data?.dob, ['YYYY-MM-DD', 'YYYY/MM/DD', 'YYYY.MM.DD', 'DD-MM-YYYY', 'DD/MM/YYYY', 'DD.MM.YYYY'], true);
      let today = moment();

      if(deviceCodingId.value === undefined || deviceCodingId.value === null) {
        deviceCodingId.setErrors({di_deviceCodingIdInvalid: true});
        errors['di_deviceCodingIdInvalid'] = true;
      }

      if((!checkDate(implantDate.value).isValid())) {
        implantDate.setErrors({di_implantDateInvalid: true});
        errors['di_implantDateInvalid'] = true;
      } else {
        if(checkDate(implantDate.value).isBefore(patientDOB)) {
          implantDate.setErrors({di_implantDateAfterDOB: true});
          errors['di_implantDateAfterDOB'] = true;
        } else if(checkDate(implantDate.value).isSameOrAfter(today)) {
          implantDate.setErrors({di_implantDateBeforeToday: true});
          errors['di_implantDateBeforeToday'] = true;
        } else {
          implantDate.setErrors(null);
        }
      }

      return Object.keys(errors).length > 0 ? errors : null;
    }
  }

  public btnDIOptions = signal<ButtonOptions>(
    new ButtonOptions({
      id: 'mdBtn',
      tooltip: 'add_device',
    })
  );

  protected modOptions = model<ModalOptions>(
    new ModalOptions({
      id: 'modal',
      title: 'add_device',
      type: ModalTypes.form,
      form: FormComponent,
      options: {
        animation: true,
        size: 'xl',
        fullscreen: "lg",
        backdrop: 'static',
        keyboard: false,
      },
      onClose: () => {this.onFormReset()}
    })
  );

  protected deviceDescDDOptions = signal<DropdownOptions<Coding | ATC>>(
    new DropdownOptions<Coding | ATC>({
      id: 'deviceDescription',
      name: 'deviceDescription',
      placeholder: 'device_description',
      label: {
        id: "device_desc_label",
        text: 'device_description',
      },
      api: {
        path: '/get-all_codings',
        headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
        body: {
          oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.8',
          description: ''
        },
      },
      required: true,
      searchComponent: DefaultSearchComponent,
      formControl: this.form.get('coding_id') as FormControl
    })
  );

  protected implantDateOptions = signal<DateOptions>(
    new DateOptions({
      id: 'implantDate',
      label: {
        text: 'implant_date',
      },
      name: 'implantDate',
      initialValue: '',
      formControl: this.form.get('implant_date') as FormControl,
      required: true,
      minDate: this.patientService.patient?.data?.dob,
      maxDate: moment().format('YYYY-MM-DD'),
    })
  );

  ngOnInit() {
    this.btnDIOptions().onClick = (event) => {
      this.mod.options().onReset = this.onFormReset;
      this.deviceDesc.detectChanges();
      this.addDeviceButton.blur();
      this.btnDIOptions().isLoading = true;
      this.downloadDropDownData(this.mod.options().title).then((value) => {
        this.btnDIOptions().isLoading = false;
        this.changeDetectorRef.detectChanges();
        this.mod.open();
      });
    };

    this.implantDateOptions().formControl = (this.form.get('implant_date') as FormControl);
    this.modOptions().onSave = this.onSave;
    this.modOptions().onShow = this.downloadDropDownData;
  }

  onFormReset = () => {
    console.log('on reset form');
    this.form.reset();
    this.deviceDesc.clearSelection();
    this.deviceDate.reset();

    this.modOptions().errors = [];
  }

  ngAfterViewInit(): void {

  }

  protected downloadDropDownData = async (title?: string) => {
    let ddData = JSON.parse(localStorage.getItem('dropDownData')!) as Object || {};

    if(ddData.hasOwnProperty('deviceDescription')) {
      return;
    }

    const data = await lastValueFrom(this.apiService.call({
      path: '/get-all_codings',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      body: {
        oid: '1.3.6.1.4.1.12559.11.10.1.3.1.42.8',
        description: ''
      }
    })!);

    if (data && data.status === 1 && data.data.length > 0) {
      Object.defineProperty(ddData, 'deviceDescription', {
        value: (data as Response<Coding>).data,
        enumerable: true,
        writable: false
      });
      this.deviceDesc.data = (data as Response<Coding>).data || [];

      localStorage.setItem('dropDownData', JSON.stringify(ddData));
    }
  }

  onSave = () => {
    this.devicesFormEl?.ngSubmit.emit();
  }

  validate = (): boolean => {
    Object.keys(this.form.controls).forEach((control) => {
      this.form.controls[control].markAsTouched();
    })

    return true;
  }

  onFormSubmit = () => {
    if(this.validate() && this.form.invalid) {
      console.log('this.form.is.invalid');
      this.modOptions().errors = this.form.errors!;
      this.mod.detectChanges();
      return;
    }

    this.modOptions().disableOnSave = true;
    let body = {
      implant_date: (this.form.get('implant_date') as FormControl).value,
      coding_id: this.deviceDescDDOptions().value!.coding_id,
      status: 1,
      version_id: this.patientService.version!.new,
    }

    this.apiService.call({
      path: '/medical-devices',
      headers: new HttpHeaders().set('pToken', this.patientService.pToken || ''),
      type: HTTPMethods.POST,
      body: body
    })?.subscribe((res: ResponseSingle<MedicalDevice>) => {
      if(res && res.status?.toString() === '1') {
        let data = this.data()?.tblOptions().data;
        data.unshift(res.data);
        if (this.data && this.data() && this.data().tbl && this.data().tbl.tableData) {
          this.data()!.tbl!.tableData! = data;
        }
        this.mod.close();
      }
    })
  }

  protected readonly assets = assets;
}
